import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchMyWorkspaces, fetchWorkspaceRole, Workspace } from '@/api/workspaces.api';
import { useAuth } from '@/hooks/useAuth';

// Re-export Workspace type for backward compatibility
export type { Workspace };

interface WorkspaceContextValue {
  currentWorkspace: Workspace | null;
  workspace: Workspace | null; // Alias for currentWorkspace
  workspaces: Workspace[];
  switchWorkspace: (slug: string) => void;
  isLoading: boolean;
  workspaceRole: string | null; // User's role in current workspace (from workspace_members.role_code)
  isAdmin: boolean; // true if workspaceRole === 'admin' || workspaceRole === 'owner'
  isOwner: boolean; // true if workspaceRole === 'owner'
}

export const WorkspaceContext =
  createContext<WorkspaceContextValue | null>(null);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { workspaceSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Use React Query to fetch workspaces (will use cached data from bootstrap)
  const {
    data: workspaces = [],
    isLoading,
    refetch,
  } = useQuery<Workspace[]>({
    queryKey: ['workspaces', 'my'],
    queryFn: fetchMyWorkspaces,
    enabled: !!user, // Only fetch when user is authenticated
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  // Find current workspace based on slug or default
  const currentWorkspace = useMemo(() => {
    if (!workspaces.length) return null;
    
    return (
      workspaces.find((w) => w.slug === workspaceSlug) ||
      workspaces.find((w) => w.is_default) ||
      workspaces[0] // Fallback to first workspace
    );
  }, [workspaces, workspaceSlug]);

  // Fetch current user's role in the workspace
  // This comes from workspace_members.role_code
  // Always defaults to null (never undefined)
  const {
    data: roleData,
    isLoading: isLoadingRole,
  } = useQuery<string | null>({
    queryKey: ['workspace-role', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return null;
      return await fetchWorkspaceRole(currentWorkspace.id);
    },
    enabled: !!currentWorkspace?.id && !!user,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  // Ensure workspaceRole is always null (never undefined)
  const workspaceRole: string | null = roleData ?? null;

  // Compute isAdmin and isOwner from workspaceRole
  const isAdmin = useMemo(() => {
    return workspaceRole === 'admin' || workspaceRole === 'owner';
  }, [workspaceRole]);

  const isOwner = useMemo(() => {
    return workspaceRole === 'owner';
  }, [workspaceRole]);

  // Redirect to workspace if slug doesn't match
  useEffect(() => {
    if (!isLoading && workspaces.length > 0 && currentWorkspace) {
      if (!workspaceSlug || workspaceSlug !== currentWorkspace.slug) {
        navigate(`/${currentWorkspace.slug}`, { replace: true });
      }
    }
  }, [isLoading, workspaces, currentWorkspace, workspaceSlug, navigate]);

  const switchWorkspace = (slug: string) => {
    navigate(`/${slug}`);
  };

  return (
    <WorkspaceContext.Provider
      value={{
        currentWorkspace,
        workspace: currentWorkspace, // Alias for currentWorkspace
        workspaces,
        switchWorkspace,
        isLoading: isLoading || isLoadingRole,
        workspaceRole, // Always null (never undefined)
        isAdmin,
        isOwner,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used inside WorkspaceProvider');
  }
  return ctx;
};
